import { Inject, Injectable, Injector, DOCUMENT } from '@angular/core';
import { Route, Router } from '@angular/router';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoggingService } from '@logging/logging.service';
import { AuthenticationService } from './authentication.service';
import { AbandonmentService } from '@auth-n/abandonment/abandonment.service';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { UiVersionStateService } from '@security/authorization/ui-version.state-service';
import { environment } from '@env/environment';
import { getAmplifyCookieStorage } from '@domain/amplify-cookie-storage';
import { resolveCookieDomain } from '@domain/base-domain';
import { describeTokenRefreshFailure, installPoolAwareTokenRefresher, isRefreshTokenExpired } from '@auth-n/amplify-token-refresher';

@Injectable({ providedIn: 'root' })
@UntilDestroy()
export class ApplicationInitialisationService {
  private isCheckingAuthentication = false;

  constructor(
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService,
    @Inject(DOCUMENT) private document: Document,
    private authenticationService: AuthenticationService,
    private abandonmentService: AbandonmentService,
    private uiVersionStateService: UiVersionStateService,
    private injector: Injector,
    private logger: LoggingService
  ) {
    logger.trace('ApplicationInitialisationService: constructing');
  }

  initialise = () => {
    this.configureAmplifyJS();
    this.setLocalUiVersion();

    this.featureFlagService
      .getValue$('underMaintenance')
      .pipe(untilDestroyed(this))
      .subscribe((flaggedUnderMaintenance) => {
        this.logger.trace('ApplicationInitialisationService: under maintenance?', flaggedUnderMaintenance);

        if (flaggedUnderMaintenance) {
          this.logger.trace('ApplicationInitialisationService: under maintenance — redirecting');
          this.injector.get(Router).navigateByUrl('/under-maintenance').then();
        } else this.checkAuthentication();
      });

    this.featureFlagService
      .getValue$('currentUiVersion')
      .pipe(untilDestroyed(this))
      .subscribe((currentUiVersion) => {
        this.uiVersionStateService.setCurrentVersion(currentUiVersion as string);
      });
  };

  private setLocalUiVersion() {
    let comments = this.getHtmlComments();
    if (comments.length > 0) {
      this.uiVersionStateService.setLocalVersion(comments.pop());
    }
  }

  private getHtmlComments(): string[] {
    let comments: string[] = [];
    let curNode: Node;
    let iterator = this.document.createNodeIterator(this.document.getRootNode(), NodeFilter.SHOW_COMMENT);

    while ((curNode = iterator.nextNode())) comments.push(curNode.nodeValue.trim());

    return comments;
  }

  /**
   * Configure the app authentication provider (AWS AmplifyJS).
   * Amplify's token storage is the hybrid adapter: the id and refresh tokens are domain-scoped
   * cookies shared across subdomains, while the access token and Amplify's client-side
   * bookkeeping keys stay in this origin's localStorage (see HybridAmplifyStorage).
   */
  private configureAmplifyJS = () => {
    this.clearLegacyAmaryllisCookies();

    Amplify.configure(environment.cognitoConfig);

    // Always use the hybrid cookie + localStorage token storage
    const cookieStorage = getAmplifyCookieStorage();
    cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage);
    this.logger.trace('ApplicationInitialisationService: configured amplify with hybrid cookie/localStorage token storage');

    // Refresh against the pool that issued the stored tokens. Reconfiguring for SSO later leaves the
    // token store on the standard pool, so SSO tokens live under the standard key prefix and, without
    // this, their first refresh is sent to the wrong pool and Amplify clears the session.
    installPoolAwareTokenRefresher({ onRefreshFailure: (error) => this.onTokenRefreshFailure(error) });

    this.logger.trace('ApplicationInitialisationService: configured amplify', environment.cognitoConfig.Auth, Amplify.getConfig());
  };

  /**
   * An expired refresh token is the normal end of a session, so it is kept out of Rollbar: log it to the
   * console for debugging and send the user to sign in again, back to the same page afterwards. Any other refresh failure is unexpected.
   */
  private onTokenRefreshFailure(error: unknown): void {
    if (isRefreshTokenExpired(error)) {
      console.log(describeTokenRefreshFailure(error), error);
      this.authenticationService.redirectToSignInAfterSessionExpiry();
      return;
    }
    this.logger.error(new Error(describeTokenRefreshFailure(error)));
  }

  /**
   * Temporary: removes legacy `amaryllis*` cookies that were replaced by
   * environment-prefixed cookies. Safe to remove once all users have loaded
   * the app at least once after this deployment.
   */
  private clearLegacyAmaryllisCookies(): void {
    const resolvedCookieDomain = resolveCookieDomain(location.hostname, environment.baseDomains);
    const cookieDomain = resolvedCookieDomain ? `; domain=${resolvedCookieDomain}` : '';
    const securePart = location.protocol === 'https:' ? '; Secure' : '';
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:01 GMT';

    this.document.cookie
      .split(';')
      .map((c) => c.split('=')[0].trim())
      .filter((name) => name.startsWith('amaryllis'))
      .forEach((name) => {
        this.document.cookie = `${name}=; ${expiry}; path=/; SameSite=Lax${cookieDomain}${securePart}`;
        this.document.cookie = `${name}=; ${expiry}; path=/; SameSite=Lax${securePart}`;
      });
  }

  /**
   * Try and set up an authentic user from the security cache, or navigate to sign-in.
   * Skips auth checks when the current route is configured with `skipAuthCheck: true`
   * to avoid racing with routes that perform their own sign-out (e.g. /logout).
   */
  private checkAuthentication = async () => {
    if (this.isCheckingAuthentication) return;
    this.isCheckingAuthentication = true;

    const allCookieNames = this.document.cookie
      .split(';')
      .map((c) => c.trim().split('=')[0])
      .filter(Boolean);
    this.logger.trace(
      'ApplicationInitialisationService: checkAuthentication called',
      'pathname:',
      window.location.pathname,
      'search:',
      window.location.search,
      'href:',
      window.location.href,
      'hostname:',
      window.location.hostname,
      'allCookies:',
      allCookieNames
    );

    if (this.shouldSkipAuthCheck()) {
      this.logger.trace('ApplicationInitialisationService: route has skipAuthCheck, skipping auth check');
      return;
    }

    const cognitoCookies = this.document.cookie.split(';').filter((c) => c.trim().startsWith('CognitoIdentityServiceProvider'));
    this.logger.trace(
      'ApplicationInitialisationService: Cognito cookies present:',
      cognitoCookies.length,
      cognitoCookies.map((c) => c.trim().split('=')[0])
    );

    if (this.abandonmentService.isAbandoned()) {
      this.logger.trace('ApplicationInitialisationService: session abandoned, signing out');
      // Reset the timer before signing out to prevent a redirect loop when the login page initialises
      this.abandonmentService.resetTimeout();
      this.authenticationService.signOutAndRedirect();
    } else {
      this.logger.trace('ApplicationInitialisationService: calling checkForAuthenticatedUser');
      this.authenticationService.checkForAuthenticatedUser();
    }
  };

  private shouldSkipAuthCheck(): boolean {
    const router = this.injector.get(Router);
    const pathname = window.location.pathname.replace(/^\//, '');
    return this.hasSkipAuthCheckFlag(router.config, pathname);
  }

  private hasSkipAuthCheckFlag(routes: Route[], pathname: string): boolean {
    for (const route of routes) {
      if (route.path === pathname && route.data?.['skipAuthCheck'] === true) {
        return true;
      }
      if (route.children) {
        if (this.hasSkipAuthCheckFlag(route.children, pathname)) {
          return true;
        }
      }
    }
    return false;
  }
}
